/**
 * Dashboard Statistics Service
 * Handles all dashboard statistics data fetching and processing
 */

import axios from 'axios';

class StatsService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Get all dashboard statistics with caching
   */
  async getAllStats(token) {
    const cacheKey = 'dashboard-stats';
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return { ...cached.data, fromCache: true };
    }

    try {
      // Ensure authorization header is set
      if (token && !axios.defaults.headers.common['Authorization']) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      // Fetch all stats in parallel
      const [inventoryRes, reservationsRes, customersRes, paymentsRes] = await Promise.allSettled([
        axios.get('/api/inventory/items/stats/overview'),
        axios.get('/api/reservations/integration/stats/overview'),
        axios.get('/api/customers/stats/overview'),
        axios.get('/api/payments/stats/overview')
      ]);

      // Process results, handling failures gracefully
      const stats = {
        inventory: this.processResult(inventoryRes),
        reservations: this.processResult(reservationsRes),
        customers: this.processResult(customersRes),
        payments: this.processResult(paymentsRes),
        lastUpdated: new Date(),
        fromCache: false
      };

      // Cache the results
      this.cache.set(cacheKey, {
        data: stats,
        timestamp: Date.now()
      });

      return stats;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw new Error(`Failed to load dashboard statistics: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Process individual stat API results
   */
  processResult(result) {
    if (result.status === 'fulfilled') {
      return result.value.data;
    } else {
      console.warn('Stats API call failed:', result.reason);
      return this.getDefaultStats();
    }
  }

  /**
   * Get default stats when API calls fail
   */
  getDefaultStats() {
    return {
      total: 0,
      active: 0,
      available: 0,
      statusCounts: {
        AVAILABLE: 0,
        IN_USE: 0,
        MAINTENANCE_REQUIRED: 0,
        OUT_OF_SERVICE: 0
      },
      totalAmount: 0,
      maintenanceDue: 0
    };
  }

  /**
   * Get specific stat category
   */
  async getInventoryStats(token) {
    try {
      if (token && !axios.defaults.headers.common['Authorization']) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      const response = await axios.get('/api/inventory/items/stats/overview');
      return response.data;
    } catch (error) {
      console.error('Error fetching inventory stats:', error);
      return this.getDefaultStats();
    }
  }

  async getReservationStats(token) {
    try {
      if (token && !axios.defaults.headers.common['Authorization']) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      const response = await axios.get('/api/reservations/integration/stats/overview');
      return response.data;
    } catch (error) {
      console.error('Error fetching reservation stats:', error);
      return this.getDefaultStats();
    }
  }

  async getCustomerStats(token) {
    try {
      if (token && !axios.defaults.headers.common['Authorization']) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      const response = await axios.get('/api/customers/stats/overview');
      return response.data;
    } catch (error) {
      console.error('Error fetching customer stats:', error);
      return this.getDefaultStats();
    }
  }

  async getPaymentStats(token) {
    try {
      if (token && !axios.defaults.headers.common['Authorization']) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      const response = await axios.get('/api/payments/stats/overview');
      return response.data;
    } catch (error) {
      console.error('Error fetching payment stats:', error);
      return this.getDefaultStats();
    }
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Force refresh stats
   */
  async refreshStats(token) {
    this.clearCache();
    return await this.getAllStats(token);
  }
}

const statsService = new StatsService();
export default statsService;